import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Create Store Form
 * Form for creating a new e-commerce store
 */
import React, { useState, useEffect } from 'react';
import { AlertCircle, Loader, Upload } from 'lucide-react';
import { EcommerceService } from '@/services/ecommerce.service';
import { supabasePermissionsService } from '@/services/supabase-permissions.service';
import { supabase } from '@/lib/supabase-client';
import { useAuth } from '@/hooks/useAuth';
export function CreateStoreForm({ organizationId, onStoreCreated, onCancel, }) {
    const { user } = useAuth();
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrgId, setSelectedOrgId] = useState(organizationId || '');
    const [formData, setFormData] = useState({
        organizationId: organizationId || '',
        name: '',
        slug: '',
        description: '',
        domain: '',
        logoUrl: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isFetchingOrgs, setIsFetchingOrgs] = useState(true);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);
    // Fetch organizations using the proper service
    useEffect(() => {
        async function loadOrganizations() {
            if (!user)
                return;
            try {
                setIsFetchingOrgs(true);
                const userOrgs = await supabasePermissionsService.getUserOrganizations(user.id);
                // Transform to Organization type
                const orgs = userOrgs.map((org) => ({
                    id: org.id,
                    name: org.name,
                    avatar_url: org.avatar_url,
                }));
                setOrganizations(orgs);
                // Set default org if provided
                if (organizationId) {
                    setSelectedOrgId(organizationId);
                    setFormData((prev) => ({
                        ...prev,
                        organizationId,
                    }));
                }
                else if (orgs.length > 0) {
                    // Auto-select first organization if not provided
                    setSelectedOrgId(orgs[0].id);
                    setFormData((prev) => ({
                        ...prev,
                        organizationId: orgs[0].id,
                    }));
                }
            }
            catch (err) {
                console.error('Error fetching organizations:', err);
                setError('Failed to load organizations');
            }
            finally {
                setIsFetchingOrgs(false);
            }
        }
        loadOrganizations();
    }, [user, organizationId]);
    // Set organization logo as default preview when org is selected
    useEffect(() => {
        if (selectedOrgId && organizations.length > 0) {
            const selectedOrg = organizations.find((org) => org.id === selectedOrgId);
            if (selectedOrg?.avatar_url) {
                setLogoPreview(selectedOrg.avatar_url);
                setFormData((prev) => ({
                    ...prev,
                    logoUrl: selectedOrg.avatar_url || '',
                }));
            }
        }
    }, [selectedOrgId, organizations]);
    // Auto-generate slug from name
    const generateSlug = (name) => {
        return name
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^\w-]/g, '')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
    };
    const handleNameChange = (e) => {
        const name = e.target.value;
        setFormData((prev) => ({
            ...prev,
            name,
            slug: generateSlug(name),
        }));
        // Clear validation error for name
        setValidationErrors((prev) => {
            const { name: _, ...rest } = prev;
            return rest;
        });
    };
    const handleSlugChange = (e) => {
        setFormData((prev) => ({
            ...prev,
            slug: e.target.value.toLowerCase().replace(/[^\w-]/g, ''),
        }));
        // Clear validation error for slug
        setValidationErrors((prev) => {
            const { slug: _, ...rest } = prev;
            return rest;
        });
    };
    const handleOrgChange = (e) => {
        const orgId = e.target.value;
        setSelectedOrgId(orgId);
        setFormData((prev) => ({
            ...prev,
            organizationId: orgId,
        }));
    };
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear validation error for this field
        setValidationErrors((prev) => {
            const { [name]: _, ...rest } = prev;
            return rest;
        });
    };
    const validateForm = () => {
        const errors = {};
        if (!formData.name.trim()) {
            errors.name = 'Store name is required';
        }
        if (!formData.slug.trim()) {
            errors.slug = 'Store slug is required';
        }
        else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
            errors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
        }
        if (!selectedOrgId) {
            errors.organizationId = 'Please select an organization';
        }
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };
    const handleLogoChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setError('Logo file must be less than 5MB');
                return;
            }
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setError('File must be an image');
                return;
            }
            setLogoFile(file);
            setError(null);
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };
    const uploadLogo = async (file) => {
        if (!file)
            return null;
        try {
            setIsUploadingLogo(true);
            const fileExt = file.name.split('.').pop();
            const fileName = `${Date.now()}.${fileExt}`;
            const filePath = `store-logos/${fileName}`;
            const { error: uploadError } = await supabase.storage
                .from('organization-logos')
                .upload(filePath, file);
            if (uploadError) {
                throw uploadError;
            }
            // Get public URL
            const { data } = supabase.storage.from('organization-logos').getPublicUrl(filePath);
            return data.publicUrl;
        }
        catch (err) {
            console.error('Error uploading logo:', err);
            throw new Error('Failed to upload logo');
        }
        finally {
            setIsUploadingLogo(false);
        }
    };
    const removeLogo = () => {
        setLogoFile(null);
        setLogoPreview(null);
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        if (!validateForm())
            return;
        if (!user || !selectedOrgId)
            return;
        try {
            setIsLoading(true);
            // Upload logo if provided
            let logoUrl = '';
            if (logoFile) {
                logoUrl = await uploadLogo(logoFile) || '';
            }
            // Create store with logo URL
            const store = await EcommerceService.createStore(selectedOrgId, {
                ...formData,
                logoUrl: logoUrl || formData.logoUrl,
            }, user.id);
            onStoreCreated?.(store);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to create store';
            setError(message);
            console.error('Error creating store:', err);
        }
        finally {
            setIsLoading(false);
        }
    };
    if (isFetchingOrgs) {
        return (_jsxs("div", { className: "flex items-center justify-center py-8", children: [_jsx(Loader, { className: "w-5 h-5 animate-spin text-blue-500 mr-2" }), _jsx("p", { className: "text-gray-600", children: "Loading organizations..." })] }));
    }
    if (organizations.length === 0) {
        return (_jsx("div", { className: "rounded-lg bg-red-50 border border-red-200 p-4", children: _jsxs("div", { className: "flex gap-3", children: [_jsx(AlertCircle, { className: "w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" }), _jsxs("div", { children: [_jsx("p", { className: "text-red-900 font-medium mb-1", children: "No Organizations Found" }), _jsx("p", { className: "text-red-800 text-sm", children: "You don't have access to any organizations yet. Please ask your administrator to add you to an organization, or create a new one." })] })] }) }));
    }
    // Only show organization selector if multiple orgs available and not pre-selected
    const showOrgSelector = organizations.length > 1 && !organizationId;
    return (_jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [error && (_jsxs("div", { className: "rounded-lg bg-red-50 border border-red-200 p-4 flex gap-3", children: [_jsx(AlertCircle, { className: "w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" }), _jsx("p", { className: "text-red-800", children: error })] })), showOrgSelector ? (_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-900 mb-2", children: "Organization *" }), _jsxs("select", { value: selectedOrgId, onChange: handleOrgChange, disabled: isLoading, className: `w-full px-4 py-2 border rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${validationErrors.organizationId
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-300'}`, children: [_jsx("option", { value: "", children: "Select an organization..." }), organizations.map((org) => (_jsx("option", { value: org.id, children: org.name }, org.id)))] }), validationErrors.organizationId && (_jsx("p", { className: "mt-1 text-sm text-red-600", children: validationErrors.organizationId }))] })) : selectedOrgId && organizations.length > 0 ? (_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-900 mb-2", children: "Organization" }), _jsx("div", { className: "px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-gray-900 font-medium", children: organizations.find((org) => org.id === selectedOrgId)?.name ||
                            'Selected Organization' }), _jsx("p", { className: "text-xs text-gray-600 mt-1", children: "Store will be created under this organization" })] })) : null, _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-900 mb-2", children: "Store Name *" }), _jsx("input", { type: "text", name: "name", value: formData.name, onChange: handleNameChange, placeholder: "e.g., My Gold Store", disabled: isLoading, className: `w-full px-4 py-2 border rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${validationErrors.name
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-300'}` }), validationErrors.name && (_jsx("p", { className: "mt-1 text-sm text-red-600", children: validationErrors.name }))] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-900 mb-2", children: "Store Slug * (URL-friendly)" }), _jsx("input", { type: "text", name: "slug", value: formData.slug, onChange: handleSlugChange, placeholder: "e.g., my-gold-store", disabled: isLoading, className: `w-full px-4 py-2 border rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${validationErrors.slug
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-300'}` }), validationErrors.slug && (_jsx("p", { className: "mt-1 text-sm text-red-600", children: validationErrors.slug })), _jsx("p", { className: "mt-1 text-xs text-gray-500", children: "Auto-generated from store name (lowercase, hyphens only)" })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-900 mb-2", children: "Description (optional)" }), _jsx("textarea", { name: "description", value: formData.description || '', onChange: handleInputChange, placeholder: "Describe your store...", disabled: isLoading, rows: 3, className: "w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed" })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-900 mb-2", children: "Domain (optional)" }), _jsx("input", { type: "text", name: "domain", value: formData.domain || '', onChange: handleInputChange, placeholder: "e.g., goldgrams.com", disabled: isLoading, className: "w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed" })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-900 mb-2", children: "Store Logo (optional)" }), logoPreview ? (_jsxs("div", { className: "mb-4 relative", children: [_jsx("div", { className: "w-32 h-32 bg-gray-100 rounded-lg overflow-hidden border-2 border-blue-300 flex items-center justify-center", children: _jsx("img", { src: logoPreview, alt: "Logo preview", className: "w-full h-full object-cover" }) }), _jsx("button", { onClick: removeLogo, disabled: isLoading || isUploadingLogo, className: "mt-2 text-sm text-red-600 hover:text-red-700 underline disabled:text-gray-400", children: "Remove logo" })] })) : (_jsxs("label", { className: "relative block w-full px-6 py-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 cursor-pointer transition disabled:opacity-50 disabled:cursor-not-allowed", children: [_jsx("input", { type: "file", accept: "image/*", onChange: handleLogoChange, disabled: isLoading || isUploadingLogo, className: "hidden" }), _jsxs("div", { className: "flex flex-col items-center justify-center", children: [_jsx(Upload, { className: "w-8 h-8 text-gray-400 mb-2" }), _jsx("p", { className: "text-sm font-medium text-gray-700", children: "Click to upload logo" }), _jsx("p", { className: "text-xs text-gray-500 mt-1", children: "PNG, JPG, GIF (max 5MB)" })] })] }))] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-900 mb-2", children: "Or provide Logo URL" }), _jsx("input", { type: "url", name: "logoUrl", value: formData.logoUrl || '', onChange: handleInputChange, placeholder: "https://...", disabled: isLoading || !!logoFile, className: "w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed" }), _jsx("p", { className: "text-xs text-gray-500 mt-1", children: logoFile ? 'File upload selected - URL will be ignored' : 'Leave empty if uploading file above' })] }), _jsxs("div", { className: "flex gap-3 pt-4", children: [_jsxs("button", { type: "submit", disabled: isLoading || isFetchingOrgs || isUploadingLogo, className: "flex-1 bg-blue-600 text-white font-medium py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition", children: [(isLoading || isUploadingLogo) && _jsx(Loader, { className: "w-4 h-4 animate-spin" }), isUploadingLogo ? 'Uploading Logo...' : isLoading ? 'Creating Store...' : 'Create Store'] }), onCancel && (_jsx("button", { type: "button", onClick: onCancel, disabled: isLoading, className: "flex-1 border border-gray-300 text-gray-900 font-medium py-2 px-4 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition", children: "Cancel" }))] })] }));
}
